import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useNotifications } from "@/hooks/useNotifications";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { FolderOpen, FileText, AlertCircle, Package } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

const priorityColors: Record<string, string> = {
  urgent: "border-destructive/50 bg-destructive/5",
  high: "border-amber-400/50 bg-amber-50",
  normal: "border-primary/20",
  low: "border-border",
};

const priorityLabel: Record<string, { icon: string; text: string }> = {
  urgent: { icon: "🔴", text: "OVERDUE" },
  high: { icon: "🟡", text: "TODAY" },
  normal: { icon: "🟡", text: "THIS WEEK" },
  low: { icon: "🔵", text: "WAITING" },
};

export const PMDashboardView = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: notifications = [] } = useNotifications();

  const { data: projects = [] } = useQuery({
    queryKey: ["pm_projects", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("projects")
        .select("id, name, project_number, status, customers(name)")
        .eq("status", "active")
        .order("created_at", { ascending: false })
        .limit(12);
      return data ?? [];
    },
    enabled: !!user,
  });

  const ballInCourt = notifications.filter((n) => n.status === "unread");

  const kpis = [
    { label: "My Active Projects", value: projects.length, icon: FolderOpen },
    { label: "WOs Awaiting Assignment", value: notifications.filter((n) => n.entity_type === "work_order").length, icon: FileText },
    { label: "Open RFIs", value: notifications.filter((n) => n.entity_type === "rfi").length, icon: AlertCircle },
    { label: "Ball in Court", value: ballInCourt.length, icon: Package,
      color: ballInCourt.length > 0 ? "text-amber-600" : "text-emerald-600" },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Project Manager Dashboard</h1>

      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <Card key={k.label}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{k.label}</CardTitle>
              <k.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className={cn("text-2xl font-bold", k.color)}>{k.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Ball in Court Panel */}
      <Card className={cn(ballInCourt.length > 0 && "border-amber-300")}>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            🎯 YOUR COURT — {ballInCourt.length} item{ballInCourt.length !== 1 ? "s" : ""} need your action
          </CardTitle>
        </CardHeader>
        <CardContent>
          {ballInCourt.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">✅ All caught up — nothing on your plate</p>
          ) : (
            <div className="space-y-2">
              {ballInCourt.slice(0, 8).map((n) => {
                const p = priorityLabel[n.priority] ?? priorityLabel.normal;
                return (
                  <div
                    key={n.id}
                    className={cn("flex items-center gap-3 rounded-md border p-3 transition-colors hover:bg-accent/50 cursor-pointer", priorityColors[n.priority])}
                    onClick={() => n.action_url && navigate(n.action_url)}
                  >
                    <span className="text-sm">{p.icon}</span>
                    <Badge variant="outline" className="text-[10px] shrink-0">{p.text}</Badge>
                    <span className="flex-1 text-sm truncate">{n.title}</span>
                    {n.action_label && (
                      <Button size="sm" variant="outline">{n.action_label}</Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* My Projects Grid */}
      <div>
        <h2 className="text-lg font-semibold mb-3">My Projects</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((p: any) => (
            <Card
              key={p.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/controller/projects/${p.id}`)}
            >
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 mb-1">
                  {p.project_number && (
                    <Badge variant="secondary" className="text-[10px]">{p.project_number}</Badge>
                  )}
                  <span className="text-sm font-semibold truncate">{p.name}</span>
                </div>
                {p.customers?.name && (
                  <p className="text-xs text-muted-foreground">{p.customers.name}</p>
                )}
                <Progress value={0} className="mt-3 h-1.5" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};
